import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import BannerFormModal from '../../components/admin/BannerFormModal';
import { apiRequest, getImageUrl } from '../../utils/api';

const BannersPage = () => {
    const { organizationId } = useParams();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);

    useEffect(() => {
        loadBanners();
    }, [organizationId]);

    const loadBanners = async () => {
        setLoading(true);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/banners`);
            setBanners(response.data || []);
        } catch (error) {
            console.error('Load banners error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu bannerı silmek istediğinize emin misiniz?')) {
            return;
        }

        try {
            await apiRequest(`/organizations/${organizationId}/banners/${id}`, {
                method: 'DELETE'
            });
            loadBanners();
        } catch (error) {
            alert('Banner silinirken bir hata oluştu');
        }
    };

    const getMediaTypeLabel = (type) => {
        const labels = {
            image: 'Resim',
            gif: 'GIF',
            video: 'Video'
        };
        return labels[type] || type;
    };

    return (
        <AdminLayout>
            <div>
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Bannerlar</h1>
                    <button
                        onClick={() => {
                            setEditingBanner(null);
                            setShowModal(true);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        + Yeni Banner
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : banners.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <p className="text-gray-500 mb-4">Henüz banner eklenmemiş.</p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            İlk Bannerı Ekle
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {banners.map((banner) => (
                            <div key={banner.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                                {banner.media_url && (
                                    <div className="mb-4">
                                        {banner.media_type === 'video' ? (
                                            <video
                                                src={getImageUrl(banner.media_url)}
                                                className="w-full h-48 object-cover rounded-lg"
                                                controls
                                                onError={(e) => {
                                                    console.error('Video load error:', e.target.src);
                                                }}
                                            />
                                        ) : (
                                            <img
                                                src={getImageUrl(banner.media_url)}
                                                alt={banner.title || 'Banner'}
                                                className="w-full h-48 object-cover rounded-lg"
                                                onError={(e) => {
                                                    console.error('Image load error:', e.target.src);
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{banner.title || 'Başlıksız Banner'}</h3>
                                {banner.description && (
                                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{banner.description}</p>
                                )}
                                <div className="flex items-center justify-between mb-4">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                        {getMediaTypeLabel(banner.media_type)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {banner.display_duration}s
                                    </span>
                                </div>
                                <div className="flex items-center justify-between mb-4">
                                    <span className={`px-2 py-1 rounded text-xs ${
                                        banner.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {banner.status === 'active' ? 'Aktif' : 'Pasif'}
                                    </span>
                                    {banner.start_date && banner.end_date && (
                                        <span className="text-xs text-gray-500">
                                            {new Date(banner.start_date).toLocaleDateString('tr-TR')} - {new Date(banner.end_date).toLocaleDateString('tr-TR')}
                                        </span>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => {
                                            setEditingBanner(banner);
                                            setShowModal(true);
                                        }}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                                    >
                                        Düzenle
                                    </button>
                                    <button
                                        onClick={() => handleDelete(banner.id)}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                                    >
                                        Sil
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Banner Form Modal */}
                <BannerFormModal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingBanner(null);
                    }}
                    banner={editingBanner}
                    organizationId={organizationId}
                    onSuccess={loadBanners}
                />
            </div>
        </AdminLayout>
    );
};

export default BannersPage;

